import { useState, useEffect } from "react";
import { MapPin, BadgeDollarSign, ArrowUpDown, Search, ChevronDown, X, LayoutGrid, Rows3, GitCompare, Heart, Sparkles, Car, Sun, Moon, Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SmartSearchBar } from "@/components/SmartSearchBar";

const LOCATION_OPTIONS = [
  { value: 'all', label: 'All Locations' },
  { value: 'bangkok', label: 'Bangkok' },
  { value: 'nakhon-pathom', label: 'Nakhon Pathom' },
  { value: 'taling-chan', label: 'Taling Chan' },
  { value: 'kaset-nawamin', label: 'Kaset-Nawamin' },
  { value: 'kanchanaphisek', label: 'Kanchanaphisek' },
  { value: 'latphrao', label: 'LatPhrao' },
  { value: 'ratchaphruek', label: 'Ratchaphruek' },
  { value: 'chaengwatthana', label: 'ChaengWatthana' },
  { value: 'kanlapaphruek', label: 'Kanlapaphruek' },
  { value: 'pak-kret', label: 'Pak Kret' },
];

const BRAND_OPTIONS = [
  { value: 'all', label: 'All Brands' },
  { value: 'mercedes-benz', label: 'Benz' },
  { value: 'bmw', label: 'BMW' },
  { value: 'audi', label: 'Audi' },
  { value: 'tesla', label: 'Tesla' },
];

const PRICE_OPTIONS = [
  { value: 'all', label: 'All Prices' },
  { value: '800k-1m', label: '800K - 1M' },
  { value: '1m-1.5m', label: '1M - 1.5M' },
  { value: '1.5m-2m', label: '1.5M - 2M' },
  { value: '2m-2.5m', label: '2M - 2.5M' },
  { value: '2.5m-3m', label: '2.5M - 3M' },
];

interface FilterBarProps {
  onLocationChange: (value: string) => void;
  onBrandChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onSmartSearch: (query: string) => void;
  onResetFilters: () => void;
  onGridToggle?: () => void;
  isTwoColumn?: boolean;
  onCompareToggle?: () => void;
  isCompareMode?: boolean;
  onFavoritesToggle?: () => void;
  isFavoritesMode?: boolean;
  favoritesCount?: number;
  location: string;
  brand: string;
  priceRange: string;
  sortBy: string;
  searchTerm: string;
  activeFilterCount: number;
  isSearching?: boolean;
  isSticky?: boolean;
  merged?: boolean;
}

export function FilterBar({
  onLocationChange,
  onBrandChange,
  onPriceChange,
  onSortChange,
  onSearchChange,
  onSmartSearch,
  onResetFilters,
  onGridToggle,
  isTwoColumn,
  onCompareToggle,
  isCompareMode,
  onFavoritesToggle,
  isFavoritesMode,
  favoritesCount = 0,
  location,
  brand,
  priceRange,
  sortBy,
  searchTerm,
  activeFilterCount,
  isSearching = false,
  isSticky = false,
  merged = false,
}: FilterBarProps) {
  const [searchExpanded, setSearchExpanded] = useState(false);
  const { theme, setTheme } = useTheme();
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  // Collapse search when no longer sticky
  useEffect(() => {
    if (!isSticky) setSearchExpanded(false);
  }, [isSticky]);

  return (
    <>
      <div className={cn("bg-transparent", merged ? "pl-6 pr-3 py-2 sm:pr-4 sm:py-2" : "container px-4 py-2")}>
        {/* Mobile Filter Count & Reset */}
        {!merged && activeFilterCount > 0 && (
          <div className="flex sm:hidden items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full font-medium">
                {activeFilterCount}
              </span>
              <span>active filter{activeFilterCount > 1 ? 's' : ''}</span>
            </div>
            <button
              onClick={onResetFilters}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              <span>Clear all</span>
            </button>
          </div>
        )}

        {/* Mobile Filters - Icon Only */}
        <div className={cn("flex sm:hidden items-center gap-2 overflow-x-auto scrollbar-hide", merged ? "" : "-mx-4 px-4")} style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {/* Location - Mobile */}
            <Select value={location} onValueChange={onLocationChange}>
              <SelectTrigger className="h-10 w-10 bg-card border-border/50 rounded-full shrink-0 px-0 [&>svg:last-child]:hidden transition-all duration-200 hover:scale-[1.02] flex items-center justify-center" aria-label="Filter by location">
                <MapPin className={cn("text-muted-foreground shrink-0", merged ? "h-5 w-5" : "h-[18px] w-[18px]")} />
              </SelectTrigger>
              <SelectContent className="animate-fade-in bg-popover border border-border shadow-lg z-50">
                {LOCATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Brand - Mobile */}
            <Select value={brand} onValueChange={onBrandChange}>
              <SelectTrigger className="h-10 w-10 bg-card border-border/50 rounded-full shrink-0 px-0 [&>svg:last-child]:hidden transition-all duration-200 hover:scale-[1.02] flex items-center justify-center" aria-label="Filter by brand">
                <Car className={cn("text-muted-foreground shrink-0", merged ? "h-5 w-5" : "h-[18px] w-[18px]")} />
              </SelectTrigger>
              <SelectContent className="animate-fade-in bg-popover border border-border shadow-lg z-50">
                {BRAND_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price - Mobile */}
            <Select value={priceRange} onValueChange={onPriceChange}>
              <SelectTrigger className="h-10 w-10 bg-card border-border/50 rounded-full shrink-0 px-0 [&>svg:last-child]:hidden transition-all duration-200 hover:scale-[1.02] flex items-center justify-center" aria-label="Filter by price range">
                <BadgeDollarSign className={cn("text-muted-foreground shrink-0", merged ? "h-5 w-5" : "h-[18px] w-[18px]")} />
              </SelectTrigger>
              <SelectContent className="animate-fade-in bg-popover border border-border shadow-lg z-50">
                {PRICE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort - Mobile */}
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className="h-10 w-10 bg-card border-border/50 rounded-full shrink-0 px-0 [&>svg:last-child]:hidden transition-all duration-200 hover:scale-[1.02] flex items-center justify-center" aria-label="Sort listings">
                <ArrowUpDown className={cn("text-muted-foreground shrink-0", merged ? "h-5 w-5" : "h-[18px] w-[18px]")} />
              </SelectTrigger>
              <SelectContent className="animate-fade-in bg-popover border border-border shadow-lg z-50">
                <SelectItem value="default">Recommended</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="mileage">Lowest Mileage</SelectItem>
              </SelectContent>
            </Select>

            {/* Search Icon - Only visible when sticky on mobile */}
            {/* Inline Search - Mobile */}
            <div className="relative shrink-0 flex items-center">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm.trim()) onSmartSearch(searchTerm); }}
                placeholder="Search..."
                className="h-10 w-24 bg-card border border-border/50 rounded-full pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
              <Search className="absolute left-2.5 h-[14px] w-[14px] text-muted-foreground pointer-events-none" />
            </div>

            {/* Grid Toggle - Mobile Only */}
            <button
              onClick={onGridToggle}
              aria-label={isTwoColumn ? "Switch to single column view" : "Switch to two column view"}
              className={cn(
                "h-10 w-10 rounded-full shrink-0 transition-all duration-200 hover:scale-[1.02] flex items-center justify-center sm:hidden",
                isTwoColumn ? "bg-foreground text-background" : "bg-card border-border/50"
              )}
            >
              {isTwoColumn ? (
                <LayoutGrid className="h-[18px] w-[18px] shrink-0" />
              ) : (
                <Rows3 className="h-[18px] w-[18px] text-muted-foreground shrink-0" />
              )}
            </button>

            {/* Compare Toggle - Mobile Only */}
            <button
              onClick={onCompareToggle}
              aria-label={isCompareMode ? "Exit compare mode" : "Enter compare mode"}
              className={cn(
                "h-10 w-10 rounded-full shrink-0 transition-all duration-200 hover:scale-[1.02] flex items-center justify-center sm:hidden",
                isCompareMode ? "bg-foreground text-background" : "bg-card border-border/50"
              )}
            >
              <GitCompare className="h-[18px] w-[18px] shrink-0" />
            </button>

            {/* Favorites Toggle - Mobile Only */}
            <button
              onClick={onFavoritesToggle}
              aria-label={isFavoritesMode ? "Show all listings" : `Show favorites (${favoritesCount})`}
              className={cn(
                "relative h-10 w-10 shrink-0 flex items-center justify-center rounded-full transition-all duration-200 hover:scale-[1.02]",
                isFavoritesMode ? "bg-red-500 text-white" : "bg-card border-border/50"
              )}
            >
              <Heart className={cn("h-[18px] w-[18px] shrink-0", isFavoritesMode && "fill-current")} />
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favoritesCount}
                </span>
              )}
            </button>

            {/* Theme Toggle - Mobile */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="relative h-10 w-10 rounded-full shrink-0 bg-card border border-border/50 transition-all duration-200 hover:scale-[1.02] flex items-center justify-center"
            >
              <Moon className="h-[18px] w-[18px] text-muted-foreground" />
            </button>
          </div>

          {/* Mobile Search Bar - Below Filter Tabs */}

          {/* Desktop Filter Count & Reset */}
          {!merged && activeFilterCount > 0 && (
            <div className="hidden sm:flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full font-medium">
                  {activeFilterCount} active filter{activeFilterCount > 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={onResetFilters}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-all duration-200 hover:scale-105"
              >
                <X className="h-4 w-4" />
                <span>Clear all filters</span>
              </button>
            </div>
          )}

          {/* Desktop Filters - Icon + Full Placeholder + Built-in Chevron */}
          <div className={cn("hidden sm:flex items-center justify-start lg:justify-center gap-2 overflow-x-auto scrollbar-hide", merged ? "" : "-mx-4 px-4")} style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {/* When sticky + search expanded: collapse other filters to icon-only; merged pill also keeps filters icon-only to save space */}
            {(() => {
              const compact = isSticky && searchExpanded;
              // On desktop, keep full pill (icon + label + chevron) even when merged.
              // Only collapse to icon-only when the search is expanded (compact).
              const iconOnly = compact;
              const iconSize = "h-4 w-4";
              const triggerClass = iconOnly
                ? "h-10 w-10 bg-card border-border/50 rounded-full px-0 py-0 gap-1 transition-all duration-300 hover:scale-[1.02] [&>span]:hidden flex items-center justify-center"
                : "w-auto min-w-[120px] shrink-0 h-10 bg-card border-border/50 rounded-full px-4 py-0 gap-2 transition-all duration-200 hover:scale-[1.02] [&>span]:text-foreground [&>span]:line-clamp-none [&>span]:whitespace-nowrap";
              return (
            <>
            {/* Location - Desktop */}
            <Select value={location} onValueChange={onLocationChange}>
              <SelectTrigger className={triggerClass} aria-label="Filter by location">
                <MapPin className={cn("text-muted-foreground shrink-0", iconSize)} />
                <SelectValue placeholder="All Locations">
                  {location === 'all' ? (
                    <>
                      <span className="hidden lg:inline">All Locations</span>
                      <span className="lg:hidden">All</span>
                    </>
                  ) : LOCATION_OPTIONS.find(o => o.value === location)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="animate-fade-in">
                {LOCATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Brand - Desktop */}
            <Select value={brand} onValueChange={onBrandChange}>
              <SelectTrigger className={triggerClass} aria-label="Filter by brand">
                <Car className={cn("text-muted-foreground shrink-0", iconSize)} />
                <SelectValue placeholder="All Brands">
                  {brand === 'all' ? (
                    <>
                      <span className="hidden lg:inline">All Brands</span>
                      <span className="lg:hidden">All</span>
                    </>
                  ) : BRAND_OPTIONS.find(o => o.value === brand)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="animate-fade-in">
                {BRAND_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price - Desktop */}
            <Select value={priceRange} onValueChange={onPriceChange}>
              <SelectTrigger className={triggerClass} aria-label="Filter by price range">
                <BadgeDollarSign className={cn("text-muted-foreground shrink-0", iconSize)} />
                <SelectValue placeholder="All Prices">
                  {priceRange === 'all' ? (
                    <>
                      <span className="hidden lg:inline">All Prices</span>
                      <span className="lg:hidden">All</span>
                    </>
                  ) : PRICE_OPTIONS.find(o => o.value === priceRange)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="animate-fade-in">
                {PRICE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort - Desktop */}
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className={triggerClass} aria-label="Sort listings">
                <ArrowUpDown className={cn("text-muted-foreground shrink-0", iconSize)} />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="animate-fade-in">
                <SelectItem value="default">Recommended</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="mileage">Lowest Mileage</SelectItem>
              </SelectContent>
            </Select>

            {/* Search - Desktop - SmartSearchBar (only when not sticky) */}
            {!isSticky && (
              <div className="transition-all duration-300 max-w-[220px] md:max-w-[220px] opacity-100">
                <SmartSearchBar
                  value={searchTerm}
                  onChange={onSearchChange}
                  onSearch={onSmartSearch}
                  isSearching={isSearching}
                  className="w-[160px] md:w-[220px]"
                />
              </div>
            )}

            {/* Sticky search: icon collapses, expands inline pushing filters to icon-only */}
            {isSticky && !compact && (
              <button
                onClick={() => setSearchExpanded(true)}
                aria-label="Open search"
                className={cn(
                  "h-10 w-10 rounded-full shrink-0 transition-all duration-200 hover:scale-[1.02] flex items-center justify-center animate-fade-in",
                  searchTerm ? "bg-foreground text-background" : "bg-card border-border/50"
                )}
              >
                <Search className="h-4 w-4 shrink-0" />
              </button>
            )}

            {isSticky && compact && (
              <div className="relative flex-1 min-w-[200px] max-w-[440px] animate-fade-in">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                {isSearching && (
                  <Loader2 className="absolute right-12 top-1/2 -translate-y-1/2 h-4 w-4 text-primary animate-spin" />
                )}
                <input
                  autoFocus
                  type="text"
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm.trim()) onSmartSearch(searchTerm); }}
                  placeholder="Search cars..."
                  className="h-10 w-full bg-card border border-border/50 rounded-full pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <button
                  onClick={() => setSearchExpanded(false)}
                  aria-label="Close search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted text-muted-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Compare Toggle - Desktop/Tablet */}
            <button
              onClick={onCompareToggle}
              aria-label={isCompareMode ? "Exit compare mode" : "Enter compare mode"}
              className={cn(
                "hidden sm:flex items-center gap-2 h-10 rounded-full px-3 lg:px-4 border transition-all duration-200 hover:scale-[1.02]",
                isCompareMode ? "bg-foreground text-background border-foreground" : "bg-card border-border/50"
              )}
            >
              <GitCompare className="h-4 w-4 shrink-0" />
              {!compact && <span className="text-sm font-medium hidden lg:inline">Compare</span>}
            </button>

            {/* Favorites Toggle - Desktop/Tablet */}
            <button
              onClick={onFavoritesToggle}
              aria-label={isFavoritesMode ? "Show all listings" : `Show favorites (${favoritesCount})`}
              className={cn(
                "hidden sm:flex items-center gap-2 h-10 rounded-full px-3 lg:px-4 border-border/50 transition-all duration-200 hover:scale-[1.02]",
                isFavoritesMode ? "bg-red-500 text-white" : "bg-card"
              )}
            >
              <Heart className={cn("h-4 w-4 shrink-0", isFavoritesMode && "fill-current")} />
              {!compact && <span className="text-sm font-medium hidden lg:inline">Favorites</span>}
              {favoritesCount > 0 && (
                <Badge className="bg-primary-foreground text-primary h-5 min-w-[20px] px-1.5 text-xs">
                  {favoritesCount}
                </Badge>
              )}
            </button>

            {/* Theme Toggle - Desktop */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="relative hidden sm:flex h-10 w-10 rounded-full shrink-0 bg-card border border-border/50 transition-all duration-200 hover:scale-[1.02] items-center justify-center"
            >
              <Moon className="h-4 w-4 text-muted-foreground" />
            </button>
            </>
              );
            })()}
          </div>
        </div>
    </>
  );
}
